import { create } from 'zustand';
import type { EditorState, EditorActions } from '../types';
import type { VideoTemplate } from '@/types/template';
import { DEFAULT_TEMPLATE_ID } from '@/features/editor/data/templates';

function loadNumber(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return Number(v);
  } catch { /* noop */ }
  return fallback;
}

function loadString(key: string, fallback: string | null): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return v;
  } catch { /* noop */ }
  return fallback;
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return JSON.parse(v);
  } catch { /* noop */ }
  return fallback;
}

function loadBoolean(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return v === 'true';
  } catch { /* noop */ }
  return fallback;
}

export const useEditorStore = create<EditorState & EditorActions>((set) => ({
  // State
  activePanel: null,
  leftSidebarOpen: true,
  rightSidebarOpen: false,
  activeTab: 'templates',
  sidebarWidth: loadNumber('editor:sidebarWidth', 320),
  rightSidebarWidth: loadNumber('editor:rightSidebarWidth', 320),
  timelineHeight: 250,
  sourcePreviewMediaId: null,
  templateStyles: loadJson('editor:templateStyles', {}),
  activeTemplateId: loadString('editor:activeTemplateId', DEFAULT_TEMPLATE_ID),
  customTemplates: loadJson('editor:customTemplates', []),
  cloudTemplates: [],
  timelineOpen: loadBoolean('editor:timelineOpen', true),
  templateOptions: loadJson('editor:templateOptions', { vignetteEnabled: true }),

  // Actions
  setActivePanel: (panel) => set({ activePanel: panel }),
  setLeftSidebarOpen: (open) => set({ leftSidebarOpen: open }),
  setRightSidebarOpen: (open) => set({ rightSidebarOpen: open }),
  toggleLeftSidebar: () => set((state) => ({ leftSidebarOpen: !state.leftSidebarOpen })),
  toggleRightSidebar: () => set((state) => ({ rightSidebarOpen: !state.rightSidebarOpen })),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSidebarWidth: (width) => {
    try { localStorage.setItem('editor:sidebarWidth', String(width)); } catch { /* noop */ }
    set({ sidebarWidth: width });
  },
  setRightSidebarWidth: (width) => {
    try { localStorage.setItem('editor:rightSidebarWidth', String(width)); } catch { /* noop */ }
    set({ rightSidebarWidth: width });
  },
  setTimelineHeight: (height) => set({ timelineHeight: height }),
  setSourcePreviewMediaId: (mediaId) => set({ sourcePreviewMediaId: mediaId }),
  setTemplateStyle: (templateId, style) => set((state) => {
    const newStyles = {
      ...state.templateStyles,
      [templateId]: style
    };
    try { localStorage.setItem('editor:templateStyles', JSON.stringify(newStyles)); } catch { /* noop */ }
    return { templateStyles: newStyles };
  }),
  setActiveTemplateId: (templateId) => {
    try {
      if (templateId) localStorage.setItem('editor:activeTemplateId', templateId);
      else localStorage.removeItem('editor:activeTemplateId');
    } catch { /* noop */ }
    set({ activeTemplateId: templateId });
  },
  saveCustomTemplate: (template) => set((state) => {
    const newTemplates = [...state.customTemplates.filter(t => t.id !== template.id), template];
    try { localStorage.setItem('editor:customTemplates', JSON.stringify(newTemplates)); } catch { /* noop */ }
    return { customTemplates: newTemplates };
  }),
  deleteCustomTemplate: (templateId) => set((state) => {
    const newTemplates = state.customTemplates.filter(t => t.id !== templateId);
    try { localStorage.setItem('editor:customTemplates', JSON.stringify(newTemplates)); } catch { /* noop */ }
    return { customTemplates: newTemplates };
  }),
  setCloudTemplates: (templates: VideoTemplate[]) => set({ cloudTemplates: templates }),
  setTimelineOpen: (open: boolean) => {
    try { localStorage.setItem('editor:timelineOpen', String(open)); } catch { /* noop */ }
    set({ timelineOpen: open });
  },
  toggleTimeline: () => set((state) => {
    const next = !state.timelineOpen;
    try { localStorage.setItem('editor:timelineOpen', String(next)); } catch { /* noop */ }
    return { timelineOpen: next };
  }),
  setTemplateOptions: (options) => set((state) => {
    const nextOptions = {
      ...state.templateOptions,
      ...options,
    };
    try { localStorage.setItem('editor:templateOptions', JSON.stringify(nextOptions)); } catch { /* noop */ }
    return { templateOptions: nextOptions };
  })
}));
